/**
 * Remote Control Client for Monochrome.
 * WebSocket connection for cross-device playback control.
 * Supports both LAN (host.cjs) and Cloud (Cloudflare Worker) modes.
 */

import { deviceManager } from './device-manager.js';

const HEARTBEAT_INTERVAL = 15000;
const PONG_TIMEOUT = 5000;
const MAX_MISSED_PONGS = 3;
const STATE_THROTTLE_MS = 250;
const QUEUE_DEDUP_INTERVAL_MS = 1000;

class RemoteControlClient {
    constructor() {
        this.ws = null;
        this.room = null;
        this.role = null; // 'player' | 'remote'
        this.clientId = null;
        this.connected = false;
        this.player = null;
        this.api = null;

        // Reconnection
        this.backoff = 1000;
        this.maxBackoff = 60000;

        // Heartbeat
        this.heartbeatInterval = null;
        this.missedPongs = 0;

        // State broadcasting
        this.lastStateBroadcast = 0;
        this.lastQueueSignature = '';
        this.lastQueueBroadcast = 0;

        // Remote state (for remote devices)
        this.remoteState = null;

        // Request/response tracking
        this._requestSeq = 0;
        this._pendingRequests = new Map();
    }

    /**
     * Initialize with player and API references.
     */
    init(player, api) {
        this.player = player;
        this.api = api;

        // Listen for events to broadcast state
        this._setupEventListeners();

        // Auto-reconnect on visibility/focus/online
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible' && !this.connected && this.room) {
                this.connect(this.room);
            }
        });
        window.addEventListener('online', () => {
            if (!this.connected && this.room) this.connect(this.room);
        });
        window.addEventListener('focus', () => {
            if (!this.connected && this.room) this.connect(this.room);
        });
    }

    /**
     * Derive room ID from Firebase UID.
     */
    async deriveRoomId(uid) {
        const encoder = new TextEncoder();
        const data = encoder.encode(uid);
        const hashBuffer = await crypto.subtle.digest('SHA-256', data);
        const hashArray = new Uint8Array(hashBuffer);
        // base64url encode
        const base64 = btoa(String.fromCharCode(...hashArray))
            .replace(/\+/g, '-')
            .replace(/\//g, '_')
            .replace(/=+$/, '');
        return `mc_${base64}`;
    }

    /**
     * Get the WebSocket URL.
     * Cloud mode: VITE_REMOTE_WS_BASE env var
     * LAN mode: Current host with /__remote_control path
     */
    getWsUrl(roomId) {
        const cloudBase = import.meta.env?.VITE_REMOTE_WS_BASE;
        if (cloudBase) {
            return `${cloudBase}/ws?room=${roomId}`;
        }
        // LAN mode
        const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
        return `${protocol}//${location.host}/__remote_control?room=${roomId}`;
    }

    /**
     * Connect to the remote control relay.
     */
    async connect(roomId, role = 'player', idToken = null) {
        this.room = roomId;
        this.role = role;

        if (this.ws) {
            this.ws.close();
            this.ws = null;
        }

        try {
            const url = this.getWsUrl(roomId);
            this.ws = new WebSocket(url);

            this.ws.onopen = () => {
                // Send join message
                const joinMsg = {
                    type: 'join',
                    room: roomId,
                    role,
                };
                if (idToken) joinMsg.idToken = idToken;
                this.ws.send(JSON.stringify(joinMsg));
            };

            this.ws.onmessage = (event) => {
                try {
                    const msg = JSON.parse(event.data);
                    this._handleMessage(msg);
                } catch (e) {
                    console.error('Remote control: invalid message', e);
                }
            };

            this.ws.onclose = () => {
                this._onDisconnect();
            };

            this.ws.onerror = (error) => {
                console.error('Remote control WebSocket error:', error);
            };
        } catch (error) {
            console.error('Remote control connection failed:', error);
            this._scheduleReconnect();
        }
    }

    disconnect() {
        const wasConnected = this.connected;
        this.connected = false;
        this.room = null;
        this._stopHeartbeat();
        if (this.ws) {
            // Remove onclose to prevent _onDisconnect from firing a second time
            this.ws.onclose = null;
            this.ws.close();
            this.ws = null;
        }
        if (wasConnected) {
            window.dispatchEvent(new CustomEvent('remote-disconnected'));
            deviceManager.setDesktopConnected(false);
        }
    }

    // ─── Message Handling ───

    _handleMessage(msg) {
        switch (msg.type) {
            case 'joined':
                this.clientId = msg.clientId;
                this.connected = true;
                this.backoff = 1000; // Reset backoff
                this._startHeartbeat();
                window.dispatchEvent(
                    new CustomEvent('remote-connected', {
                        detail: { room: msg.room, role: msg.role, clientId: msg.clientId },
                    })
                );
                // If player, broadcast initial state
                if (this.role === 'player') {
                    this._broadcastState();
                    this._broadcastQueue();
                }
                break;

            case 'pong':
                this.missedPongs = 0;
                break;

            case 'cmd':
                if (this.role === 'player') {
                    this._handleCommand(msg);
                }
                break;

            case 'req':
                if (this.role === 'player') {
                    this._handleRequest(msg);
                }
                break;

            case 'res':
                this._handleResponse(msg);
                break;

            case 'state':
                if (this.role === 'remote') {
                    if (msg.libraryChanged) {
                        window.dispatchEvent(new CustomEvent('remote-library-changed'));
                    } else {
                        this.remoteState = msg;
                        window.dispatchEvent(new CustomEvent('remote-state', { detail: msg }));
                    }
                }
                break;

            case 'queue':
                if (this.role === 'remote') {
                    window.dispatchEvent(new CustomEvent('remote-queue', { detail: msg }));
                }
                break;

            case 'error':
                console.error('Remote control error:', msg.message);
                break;
        }
    }

    _handleCommand(msg) {
        if (!this.player) return;
        const p = this.player;

        switch (msg.cmd) {
            case 'playPause':
                p.handlePlayPause();
                break;
            case 'pause': {
                const el = p.activeElement;
                if (el && !el.paused) el.pause();
                if (p.isPlayingYouTube && p.youtubePlayer?.pauseVideo) {
                    p.youtubePlayer.pauseVideo();
                }
                break;
            }
            case 'play': {
                const el2 = p.activeElement;
                if (el2 && el2.paused) el2.play();
                if (p.isPlayingYouTube && p.youtubePlayer?.playVideo) {
                    p.youtubePlayer.playVideo();
                }
                break;
            }
            case 'next':
                p.playNext();
                break;
            case 'prev':
                p.playPrev();
                break;
            case 'seek':
                if (typeof msg.pct === 'number') {
                    const el = p.activeElement;
                    if (el.duration) el.currentTime = msg.pct * el.duration;
                }
                break;
            case 'setVolume':
                if (typeof msg.pct === 'number') p.setVolume(msg.pct);
                break;
            case 'playTracks':
                if (msg.tracks) p.setQueue(msg.tracks, msg.startIndex || 0);
                if (msg.tracks) p.playTrackFromQueue();
                break;
            case 'playAtIndex':
                if (typeof msg.index === 'number') p.playAtIndex(msg.index);
                break;
            case 'playDirect':
                if (msg.track) {
                    p.setQueue([msg.track], 0);
                    p.playTrackFromQueue(msg.startTime || 0);
                }
                break;
            case 'startContext':
                p.startContext({
                    tracks: msg.tracks || [],
                    startIndex: msg.startIndex || 0,
                    contextMeta: msg.contextMeta || null,
                    shuffle: msg.shuffle || false,
                });
                p.playTrackFromQueue();
                break;
            case 'addToQueue':
                if (msg.track) p.addToQueue(msg.track);
                break;
            case 'addNextToQueue':
                if (msg.track) p.addNextToQueue(msg.track);
                break;
            case 'clearManualQueue':
                p.clearQueue();
                break;
            case 'removeManualQueue':
                if (typeof msg.index === 'number') p.removeFromManualQueue(msg.index);
                break;
            case 'moveManualQueue':
                if (typeof msg.fromIndex === 'number' && typeof msg.toIndex === 'number') {
                    p.moveInManualQueue(msg.fromIndex, msg.toIndex);
                }
                break;
            case 'transferPlayback':
                this._handleTransferPlayback(msg);
                break;
            case 'libraryChanged':
                window.dispatchEvent(new CustomEvent('remote-library-changed'));
                break;
        }
    }

    async _handleRequest(msg) {
        if (!this.api) return;

        const { requestId, action, payload } = msg;
        let data = null;
        let ok = true;

        try {
            const timeout = new Promise((_, reject) => setTimeout(() => reject(new Error('Request timeout')), 10000));

            const execute = async () => {
                switch (action) {
                    case 'search':
                        return this.api.search(payload?.query || '');
                    case 'getAlbum':
                        return this.api.getAlbum(payload?.id);
                    case 'getArtist':
                        return this.api.getArtist(payload?.id);
                    case 'getPlaylist':
                        return this.api.getPlaylist(payload?.id);
                    case 'getMix':
                        return this.api.getMix(payload?.id);
                    case 'getLibrary': {
                        const { db } = await import('./db.js');
                        return {
                            tracks: await db.getFavorites('track'),
                            albums: await db.getFavorites('album'),
                            artists: await db.getFavorites('artist'),
                            playlists: await db.getAll('user_playlists'),
                        };
                    }
                    case 'getUserPlaylist': {
                        const { db } = await import('./db.js');
                        return db.getPlaylist(payload?.id);
                    }
                    default:
                        throw new Error(`Unknown action: ${action}`);
                }
            };

            data = await Promise.race([execute(), timeout]);
        } catch (error) {
            ok = false;
            data = { error: error.message };
        }

        this._send({
            type: 'res',
            requestId,
            ok,
            data,
            _to: msg._from,
        });
    }

    _handleResponse(msg) {
        const pending = this._pendingRequests.get(msg.requestId);
        if (pending) {
            this._pendingRequests.delete(msg.requestId);
            if (msg.ok) {
                pending.resolve(msg.data);
            } else {
                pending.reject(new Error(msg.data?.error || 'Request failed'));
            }
        }
    }

    _handleTransferPlayback(msg) {
        if (!this.player) return;
        const p = this.player;
        const { state, position, playing } = msg;

        if (state?.tracks) {
            p.startContext({
                tracks: state.tracks,
                startIndex: state.currentIndex || 0,
                contextMeta: state.contextMeta || null,
            });
        }

        // Restore manual & autoplay queues AFTER startContext
        // (startContext clears autoplayQueue and preserves manualQueue,
        //  but we want the exact state from the transferring device)
        if (state?.manualQueue) {
            p.manualQueue = [...state.manualQueue];
        }
        if (state?.autoplayQueue) {
            p.autoplayQueue = [...state.autoplayQueue];
        }
        if (state?.shuffleActive) {
            p.shuffleActive = state.shuffleActive;
        }
        if (state?.repeatMode !== undefined) {
            p.repeatMode = state.repeatMode;
        }
        if (state?.currentTrack) {
            p.currentTrack = state.currentTrack;
        }

        p.playTrackFromQueue(position || 0);

        if (!playing) {
            const pauseWhenReady = () => {
                const el = p.activeElement;
                if (el && el.readyState >= 2) {
                    el.pause();
                } else {
                    // Wait for media to be ready before pausing
                    const handler = () => {
                        el?.removeEventListener('canplay', handler);
                        el?.pause();
                    };
                    el?.addEventListener('canplay', handler, { once: true });
                    // Fallback timeout
                    setTimeout(() => {
                        el?.removeEventListener('canplay', handler);
                        el?.pause();
                    }, 2000);
                }
            };
            pauseWhenReady();
        }
    }

    // ─── State Broadcasting ───

    _setupEventListeners() {
        const broadcastState = () => {
            if (this.role === 'player') this._broadcastState();
        };
        // Force-send bypasses throttle — used for critical state changes
        const broadcastStateForce = () => {
            if (this.role === 'player') this._broadcastState(true);
        };
        const broadcastQueue = () => {
            if (this.role === 'player') this._broadcastQueue();
        };

        // Player events — force on play/pause/seek so remotes update instantly
        const audio = document.getElementById('audio-player');
        if (audio) {
            audio.addEventListener('play', broadcastStateForce);
            audio.addEventListener('pause', broadcastStateForce);
            audio.addEventListener('seeked', broadcastStateForce);
            audio.addEventListener('volumechange', broadcastStateForce);
            audio.addEventListener('timeupdate', broadcastState);
        }

        // YouTube events
        window.addEventListener('youtube-play', broadcastStateForce);
        window.addEventListener('youtube-pause', broadcastStateForce);
        window.addEventListener('youtube-timeupdate', broadcastState);

        // Queue events (via renderQueueFunction hook)
        const origRenderQueue = window.renderQueueFunction;
        window.renderQueueFunction = () => {
            if (origRenderQueue) origRenderQueue();
            broadcastQueue();
        };
    }

    _broadcastState(force = false) {
        if (!this.connected || !this.player) return;

        const now = Date.now();
        if (!force && now - this.lastStateBroadcast < STATE_THROTTLE_MS) return;
        this.lastStateBroadcast = now;

        const p = this.player;
        const el = p.activeElement;

        let position, duration;
        if (p.isPlayingYouTube && p.youtubePlayer) {
            try {
                position = p.youtubePlayer.getCurrentTime();
                duration = p.youtubePlayer.getDuration();
            } catch {
                position = 0;
                duration = 0;
            }
        } else {
            position = el?.currentTime || 0;
            duration = el?.duration || p.currentTrack?.duration || 0;
        }

        // YouTube player states: -1=unstarted, 0=ended, 1=playing, 2=paused, 3=buffering, 5=cued
        const ytPlaying =
            p.isPlayingYouTube && p.youtubePlayer?.getPlayerState ? p.youtubePlayer.getPlayerState() === 1 : false;

        // Resolve cover to full URL (album.cover is a TIDAL ID like "abc-def", not a URL)
        const coverId = p.currentTrack?.image || p.currentTrack?.cover || p.currentTrack?.album?.cover;
        const coverUrl = coverId && this.api ? this.api.getCoverUrl(coverId) : '';

        this._send({
            type: 'state',
            playing: p.isPlayingYouTube ? ytPlaying : el ? !el.paused : false,
            title: p.currentTrack?.title || '',
            artist: p.currentTrack?.artists?.[0]?.name || p.currentTrack?.artist?.name || '',
            cover: coverUrl,
            duration,
            position,
            volume: p.userVolume,
            ts: now,
        });
    }

    _broadcastQueue() {
        if (!this.connected || !this.player) return;

        const now = Date.now();
        if (now - this.lastQueueBroadcast < QUEUE_DEDUP_INTERVAL_MS) return;

        const p = this.player;
        const queueView = p.getQueueView();

        // Deduplicate by signature — include track IDs to detect content changes
        const queue = p.getCurrentQueue();
        const sig = JSON.stringify({
            idx: p.currentQueueIndex,
            len: queue.length,
            ids: queue.slice(Math.max(0, p.currentQueueIndex - 1), p.currentQueueIndex + 5).map((t) => t.id),
            manual: p.manualQueue.map((t) => t.id),
            autoplay: p.autoplayQueue.length,
            track: p.currentTrack?.id,
        });

        if (sig === this.lastQueueSignature) return;
        this.lastQueueSignature = sig;
        this.lastQueueBroadcast = now;

        this._send({
            type: 'queue',
            contextIndex: p.currentQueueIndex,
            contextTracks: p.getCurrentQueue().map((t) => ({
                id: t.id,
                title: t.title,
                duration: t.duration,
                artist: t.artists?.[0] || t.artist,
                album: t.album,
                source: t.source,
                youtubeId: t.youtubeId,
            })),
            contextMeta: queueView.contextMeta,
            contextPointer: p.contextPointer,
            currentLayer: queueView.currentLayer,
            nowPlaying: p.currentTrack,
            manualQueue: queueView.manualQueue,
            autoplayEnabled: queueView.autoplayEnabled,
            autoplayQueue: queueView.autoplayQueue,
            repeatMode: p.repeatMode,
        });
    }

    // ─── Sending ───

    _send(payload) {
        if (this.ws?.readyState === WebSocket.OPEN) {
            try {
                this.ws.send(JSON.stringify(payload));
            } catch (e) {
                console.error('Remote control send failed:', e);
            }
        }
    }

    /**
     * Send a request to the player and wait for response.
     */
    request(action, payload = {}) {
        return new Promise((resolve, reject) => {
            const requestId = `${Date.now()}-${++this._requestSeq}`;
            const timer = setTimeout(() => {
                this._pendingRequests.delete(requestId);
                reject(new Error('Request timeout'));
            }, 10000);

            this._pendingRequests.set(requestId, {
                resolve: (data) => {
                    clearTimeout(timer);
                    resolve(data);
                },
                reject: (err) => {
                    clearTimeout(timer);
                    reject(err);
                },
            });

            this._send({ type: 'req', requestId, action, payload });
        });
    }

    /**
     * Send a command to the player.
     */
    command(cmd, extra = {}) {
        this._send({ type: 'cmd', cmd, ...extra });
    }

    // ─── Heartbeat ───

    _startHeartbeat() {
        this._stopHeartbeat();
        this.missedPongs = 0;

        this.heartbeatInterval = setInterval(() => {
            if (!this.connected) return;

            this._send({ type: 'ping' });
            this.missedPongs++;

            if (this.missedPongs >= MAX_MISSED_PONGS) {
                console.warn('Remote control: too many missed pongs, reconnecting');
                this.ws?.close();
            }
        }, HEARTBEAT_INTERVAL);
    }

    _stopHeartbeat() {
        if (this.heartbeatInterval) {
            clearInterval(this.heartbeatInterval);
            this.heartbeatInterval = null;
        }
    }

    // ─── Reconnection ───

    _onDisconnect() {
        this.connected = false;
        this._stopHeartbeat();
        window.dispatchEvent(new CustomEvent('remote-disconnected'));
        deviceManager.setDesktopConnected(false);

        if (this.room) {
            this._scheduleReconnect();
        }
    }

    _scheduleReconnect() {
        setTimeout(() => {
            if (!this.connected && this.room) {
                this.connect(this.room, this.role);
            }
        }, this.backoff);
        this.backoff = Math.min(this.backoff * 1.5, this.maxBackoff);
    }

    // ─── Device Transfer ───

    /**
     * Transfer playback from local to desktop.
     */
    castToDesktop() {
        if (!this.player || !this.connected) return;
        const p = this.player;
        const el = p.activeElement;

        const state = {
            tracks: p.getCurrentQueue(),
            currentIndex: p.currentQueueIndex,
            contextMeta: p.contextMeta,
            manualQueue: [...p.manualQueue],
            autoplayQueue: [...p.autoplayQueue],
            currentTrack: p.currentTrack,
            shuffleActive: p.shuffleActive,
            repeatMode: p.repeatMode,
        };

        const position = p.isPlayingYouTube ? p.youtubePlayer?.getCurrentTime?.() || 0 : el?.currentTime || 0;

        const playing = p.isPlayingYouTube ? !!(p.youtubePlayer?.getPlayerState?.() === 1) : !el?.paused;

        // Stop local playback — must tear down Shaka/HLS before clearing the element,
        // otherwise Shaka's MediaSource keeps streaming audio after src is cleared.
        if (p.isPlayingYouTube) {
            p.stopYouTube();
        } else {
            if (p.hls) {
                p.hls.destroy();
                p.hls = null;
            }
            if (p.shakaInitialized && p.shakaPlayer) {
                p.shakaPlayer.unload();
                p.shakaInitialized = false;
            }
            if (el) {
                el.pause();
                el.src = '';
                el.removeAttribute('src');
                el.load();
            }
        }

        deviceManager.switchToDesktop();

        this.command('transferPlayback', { state, position, playing });
    }

    /**
     * Transfer playback from desktop back to local.
     */
    async castToLocal() {
        if (!this.player) return;

        // Send explicit pause command (not toggle) to desktop
        this.command('pause');

        // Wait for next state update from desktop (or timeout)
        const rs = await new Promise((resolve) => {
            const timeout = setTimeout(() => {
                window.removeEventListener('remote-state', handler);
                resolve(this.remoteState); // Fall back to last known state
            }, 500);
            const handler = (e) => {
                clearTimeout(timeout);
                window.removeEventListener('remote-state', handler);
                resolve(e.detail);
            };
            window.addEventListener('remote-state', handler);
        });

        if (!rs) return;

        // Interpolate position from last known state
        let position = rs.position || 0;
        if (rs.playing && rs.ts) {
            const elapsed = (Date.now() - rs.ts) / 1000;
            position = Math.min(position + elapsed, rs.duration || 0);
        }

        deviceManager.switchToLocal();

        // Restore will happen via device-changed event in the player
        deviceManager.setPendingTransfer({ position, playing: rs.playing });
    }

    /**
     * Compute interpolated remote position.
     */
    computeRemotePosition() {
        if (!this.remoteState) return 0;
        if (!this.remoteState.playing) return this.remoteState.position;
        const elapsed = (Date.now() - this.remoteState.ts) / 1000;
        return Math.min(this.remoteState.position + elapsed, this.remoteState.duration);
    }

    // ─── Player Method Patching ───

    /**
     * Patch player methods to route through WebSocket when controlling desktop.
     */
    patchPlayerMethods() {
        if (!this.player) return;
        const p = this.player;
        const rc = this;

        // Store originals
        p.__lanRemoteOriginal = {
            setQueue: p.setQueue.bind(p),
            playNext: p.playNext.bind(p),
            playPrev: p.playPrev.bind(p),
            addToQueue: p.addToQueue.bind(p),
            handlePlayPause: p.handlePlayPause.bind(p),
            playTrackFromQueue: p.playTrackFromQueue.bind(p),
            playAtIndex: p.playAtIndex.bind(p),
        };

        const shouldRoute = () => deviceManager.getCurrentDevice() === 'desktop' && rc.connected;

        p.setQueue = function (tracks, startIndex, isRadio) {
            if (shouldRoute()) {
                rc.command('playTracks', { tracks, startIndex });
            } else {
                p.__lanRemoteOriginal.setQueue(tracks, startIndex, isRadio);
            }
        };

        p.playNext = function (recursiveCount) {
            if (shouldRoute()) {
                rc.command('next');
            } else {
                p.__lanRemoteOriginal.playNext(recursiveCount);
            }
        };

        p.playPrev = function (recursiveCount) {
            if (shouldRoute()) {
                rc.command('prev');
            } else {
                p.__lanRemoteOriginal.playPrev(recursiveCount);
            }
        };

        p.addToQueue = function (trackOrTracks) {
            if (shouldRoute()) {
                const tracks = Array.isArray(trackOrTracks) ? trackOrTracks : [trackOrTracks];
                tracks.forEach((t) => rc.command('addToQueue', { track: t }));
            } else {
                p.__lanRemoteOriginal.addToQueue(trackOrTracks);
            }
        };

        p.handlePlayPause = function () {
            if (shouldRoute()) {
                rc.command('playPause');
            } else {
                p.__lanRemoteOriginal.handlePlayPause();
            }
        };

        // No-op when routing — the playTracks command already triggers
        // playTrackFromQueue on the desktop after setQueue.
        p.playTrackFromQueue = function (startTime, recursiveCount, isRetry) {
            if (shouldRoute()) {
                return; // Desktop handles playback via playTracks command
            }
            return p.__lanRemoteOriginal.playTrackFromQueue(startTime, recursiveCount, isRetry);
        };

        p.playAtIndex = function (index) {
            if (shouldRoute()) {
                rc.command('playAtIndex', { index });
            } else {
                p.__lanRemoteOriginal.playAtIndex(index);
            }
        };
    }

    /**
     * Restore original player methods (for castback).
     */
    restorePlayerMethods() {
        if (!this.player?.__lanRemoteOriginal) return;
        const p = this.player;
        const orig = p.__lanRemoteOriginal;

        p.setQueue = orig.setQueue;
        p.playNext = orig.playNext;
        p.playPrev = orig.playPrev;
        p.addToQueue = orig.addToQueue;
        p.handlePlayPause = orig.handlePlayPause;
        p.playTrackFromQueue = orig.playTrackFromQueue;
        p.playAtIndex = orig.playAtIndex;

        delete p.__lanRemoteOriginal;
    }
}

export const remoteControl = new RemoteControlClient();
